import { Alert, Button } from "reactstrap";
import { DataTransforms, Module } from "../../../types/TenantInterfaces";
import AppDrawer from "../AppDrawer";
import { GetDefaultLayout } from "./ModuleLayoutSelector";
import { useMemo, useState } from "react";
import CreateRecordRendererV2 from "../../Records/CreateRecordRendererV2";
import useRecord from "../../../api/api-hooks/records/useRecord";
import useCreateRecord from "../../../api/api-hooks/records/useCreateRecord";
import AppSpinner from "../../../elements/AppSpinner";
import { SelectPicker } from "rsuite";
import useModule from "../../../hooks/useModule";
import pluralize from "pluralize";
import { buildPrefill } from "../../../utils/DataTransformsUtils";
import InstanceUtils from "../../../utils/InstanceUtils";
import { getErrorMessage } from "../../../utils/GeneralUtils";

interface Props {
  open: boolean;
  dataTransform?: DataTransforms;
  module?: Module | null;
  sourceRecordID?: string;
  onClose: () => void;
}

const empty: [] = [];

const TransformRecordDrawer = ({
  open,
  sourceRecordID,
  module,
  onClose,
}: Props) => {
  const [values, setValues] = useState<any>();
  const [prefill, setPrefill] = useState<any>();
  const [prefillLoading, setPrefillLoading] = useState<boolean>(false);
  const [errorTransforming, setErrorTransforming] = useState<string>();

  const { createRecord, clearError, recordCreating, createRecordError } =
    useCreateRecord();
  const [dataTransform, setDataTransform] = useState<
    DataTransforms | undefined
  >(undefined);

  const { record, recordLoading } = useRecord({
    moduleAPIName: module?.APIName || "",
    recordID: sourceRecordID || "",
    enabled: open,
  });
  const { module: targetModule } = useModule(
    dataTransform?.TargetModule,
    true,
    false,
    false
  );

  const availableDataTransforms = module?.DataTransforms || empty;

  const defaultLayout = useMemo(
    () => (targetModule ? GetDefaultLayout(targetModule) : null),
    [targetModule]
  );

  const prefilTransform = useMemo(() => {
    if (!record || !dataTransform || !module) return undefined;
    setPrefillLoading(true);
    return buildPrefill({
      record: record,
      sourceModule: module,
      targetModule: targetModule,
      dataTransform: dataTransform,
      onSuccess: (prefill) => {
        setPrefill(prefill);
        setValues([]);
        setPrefillLoading(false);
      },
      onError: (e: any) => {
        setPrefillLoading(false);
        setErrorTransforming(getErrorMessage(e, "Error Transforming Record"));
      },
    });
  }, [dataTransform, record]);

  return (
    <AppDrawer
      size="md"
      placement="right"
      open={open}
      onClose={() => {
        setDataTransform(undefined);
        onClose();
      }}
      backdrop={"static"}
      headerTitle={
        dataTransform?.SourceModule
          ? `Creating New ${pluralize.singular(
              dataTransform?.TargetModule || ""
            )} from ${pluralize.singular(dataTransform?.SourceModule || "")}`
          : "Convert Record"
      }
      headerOptions={
        <div>
          {!recordCreating ? (
            <Button
              disabled={!dataTransform || prefillLoading}
              onClick={convertRecord}
              color="primary"
              size="sm"
              style={{ width: 100 }}
            >
              Convert
            </Button>
          ) : (
            <AppSpinner />
          )}
        </div>
      }
      body={
        <>
          <div className="d-flex align-items-center mb-3 w-100">
            <h4 className="m-0 text-nowrap">Data Transform: </h4>
            {availableDataTransforms &&
            availableDataTransforms?.length > 0 &&
            !recordLoading ? (
              <SelectPicker
                data={
                  availableDataTransforms?.map((dt) => ({
                    label: dt.Name,
                    value: dt.id,
                  })) || empty
                }
                onChange={(value: string | null) => {
                  if (!value) return setDataTransform(undefined);
                  const dt = availableDataTransforms?.find(
                    (dt) => dt.id === value
                  );

                  setDataTransform(dt);
                }}
                searchable={false}
                className="w-100 ml-2"
                placeholder="Select Transform"
                defaultOpen
              />
            ) : (
              <div className="ml-2 p-0">No Data Transforms Available</div>
            )}
          </div>

          <Alert
            isOpen={errorTransforming || createRecordError ? true : false}
            toggle={() => {
              setErrorTransforming(undefined);
              clearError();
            }}
            color="danger"
          >
            {errorTransforming ||
              createRecordError ||
              "Error Transforming Record"}
          </Alert>

          {prefillLoading && (
            <div className="d-flex flex-column mt-5 align-items-center">
              <h2 className="py-2">Transforming your data</h2>
              <AppSpinner size="xl" />
            </div>
          )}

          {!prefillLoading &&
            dataTransform !== undefined &&
            values !== undefined &&
            prefilTransform && (
              <CreateRecordRendererV2
                key={dataTransform.id}
                module={targetModule!}
                layout={defaultLayout}
                prefill={prefilTransform}
                onChange={(values) => setValues(values)}
                variableValues={undefined}
              />
            )}
        </>
      }
    />
  );

  async function convertRecord() {
    if (!targetModule) return;
    setPrefillLoading(true);
    const transformedRecord = await InstanceUtils.formatRecordCreationPayload(
      targetModule.Fields,
      values,
      true
    );

    createRecord({
      module: targetModule,
      payload: transformedRecord,
      onCreate: () => {
        setPrefillLoading(false);
        onClose();
      },
      onError: (e) => {
        setPrefillLoading(false);
        setErrorTransforming(getErrorMessage(e, "Error Transforming Record"));
      },
    });
  }
};

export default TransformRecordDrawer;
